/* eslint-disable react-hooks/exhaustive-deps */

import { Paywall } from '@monorepo-hol/design-system';
import { UserInfo } from '@monorepo-hol/providers';

import {
  DetailPage,
  DocumentViewer,
  LoadingState,
  RequestUpgrade,
} from '../../../components';
import { capitalizeEachWord, checkIfAuthorized } from '../../../helper/utils';
import { mappingAcl } from '../../../helper/catalog';
import NotFoundPage from '../../../pages/404';
import type { DataDocumentDetail } from '../services/document-detail/types';
import type { SubmenuType } from '../../../components/types';
import { type GetDocumentDetailData } from '../services/document-detail';
import { useConsolidationData } from '../hooks/useConsolidationData';
import UnexpectedError from '../../../components/ErrorMessage/UnexpectedError/UnexpectedError';
import { MainCatalog } from '../../../types/catalog';
import { useStartTrace } from '../hooks/useStartTrace';
import { PdfProvider } from '../../../provider/PdfProvider';
import type { MetaDataProps } from '../../../types/metadata';
import { type ToolbarProps } from '../../../components/HeaderDetail/Toolbar';
import { FooterNavPdf } from '../../../components/FooterNavPdf';
import { useDownloadTrigger } from '@pd/hooks/useDownloadTrigger';
import { useDownloadPdf } from '@pd/hooks/useDownloadPdf';

export interface ConsolidationLayoutProps {
  guid: string;
  user: UserInfo;
  docId?: string;
  dataDocument: GetDocumentDetailData;
  data: DataDocumentDetail;
  subMenu: SubmenuType;
  catalogData?: MainCatalog;
  appendicesList?: any[];
  is404: boolean;
}

export default function ConsolidationLayout(props: ConsolidationLayoutProps) {
  const {
    guid,
    user,
    docId,
    dataDocument,
    data,
    subMenu,
    catalogData,
    appendicesList,
    is404,
  } = props;

  const catalogAcl = mappingAcl(data);

  useStartTrace({ guid });

  const { handleDownloadClick, isDownloadAuthorized, isUserLoggedIn } = useDownloadPdf();

  useDownloadTrigger({
    onTriggerDownload: handleDownloadClick,
    enabled: isDownloadAuthorized && isUserLoggedIn,
  });

  const {
    LangState,
    isDocumentReady,
    isDocumentError,
    pdfDoc,
    pdfDocList,
    consolidationDocument,
    consolidationDocumentList,
  } = useConsolidationData({
    guid,
    data,
    user,
    docId,
    catalogAcl,
    catalogData,
  });

  // useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     document.body.style.cssText = 'overflow: hidden;';
  //   }
  // }, []);

  const isNonLoginOrUserFree: boolean =
    (user && user?.package === 'member') || !user;

  const isToolbarShown =
    !isNonLoginOrUserFree &&
    !(
      catalogAcl?.consolidations !== undefined &&
      !checkIfAuthorized(catalogAcl?.consolidations) &&
      !!user?.jwt
    );

  const renderPaywall = () => {
    if (isNonLoginOrUserFree) {
      return (
        <Paywall
          isUserLoggedIn={user !== undefined}
          additionalQuery={{
            utm_source: 'pusat_data',
            utm_medium: 'pusatdata',
            utm_campaign: 'subscription_paywall',
          }}
          withUtmTerm
          locale={LangState.language}
        />
      );
    }
    if (
      catalogAcl?.consolidations !== undefined &&
      !checkIfAuthorized(catalogAcl?.consolidations) &&
      !!user?.jwt
    ) {
      return <RequestUpgrade type="Peraturan Konsolidasi" />;
    }
    return null;
  };

  const isNotfound =
    (data !== undefined && Object.keys(data).length === 0) ||
    dataDocument?.status === 404 ||
    !data ||
    !catalogData.mainDocument;

  const isAuthorized =
    catalogAcl?.consolidations !== undefined &&
    checkIfAuthorized(catalogAcl?.consolidations);

  const isDocumentContentShow = isDocumentReady && pdfDoc?.blob !== undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d: any = data;
  const metadata: MetaDataProps = {
    profile_guid: d.profile_guid,
    title: d.title,
    title_en: d?.title_en || d.title,
    status: d.legal_status,
    subtitle: capitalizeEachWord(d.subtitle),
    subtitle_en: d?.subtitle_en
      ? capitalizeEachWord(d.subtitle_en)
      : capitalizeEachWord(d.subtitle),
    established_at: d.established_at,
    enforced_at: d.enforced_at,
    expired_at: d.expired_at,
    ruling: d?.meta?.ruling,
    judiciary_level: d?.meta?.judiciary_level,
    pts_ketua: d?.meta?.pts_ketua,
    pts_anggota: d?.meta?.pts_anggota,
    pts_pemohon: d?.meta?.pts_pemohon,
    is_perda: d.is_perda,
    slug: d.slug,
    prt_status: d.prt_status,
  };

  const toolbarProps: ToolbarProps = {
    slug: data?.slug,
    lampiranList: appendicesList,
    isBackShown: false,
    appendicesType: 'appendices_consolidation',
    subMenu: 'konsolidasi',
    documentList: consolidationDocumentList,
    mainDocumentData: catalogData.mainDocument,
    catalogData: catalogData,
    smartDocData: undefined,
    pdfData: pdfDoc,
    fileType: pdfDoc?.type,
  };

  const renderDocumentViewer = () => {
    if (isAuthorized) {
      if (isDocumentError) {
        return (
          <div className="py-28">
            <UnexpectedError message="Terjadi Kesalahan saat memuat dokumen" />
          </div>
        );
      } else {
        return (
          <DocumentViewer
            pdfData={pdfDoc}
            smartDocData={undefined}
            documentList={consolidationDocumentList}
            documentListType="consolidation"
            subMenu="konsolidasi"
            lampiranList={appendicesList}
            appendicesType="appendices_consolidation"
            mainDocumentData={catalogData.mainDocument}
            isDocumentShowed={isDocumentContentShow}
            catalogData={catalogData}
            slug={data?.slug}
            headerDetailProps={{
              activeMenu: 'consolidations',
              catalogList: catalogData?.documentList,
              catalogListType: 'consolidation',
              subMenu,
              metadata,
              toolbarProps,
            }}
            is404={is404}
          />
        );
      }
    }
    return null;
  };

  if (isNotfound) {
    return (
      <>
        <NotFoundPage />
        <FooterNavPdf />
      </>
    );
  } else {
    return (
      <PdfProvider catalogData={catalogData}>
        <DetailPage
          data={data}
          subMenu={subMenu}
          activeMenu="consolidations"
          catalogList={catalogData?.documentList}
          catalogListType="consolidation"
          toolbarProps={toolbarProps}
          isToolbarShown={isToolbarShown && !is404}
        >
          {renderDocumentViewer()}

          {renderPaywall()}
        </DetailPage>
        <FooterNavPdf />
      </PdfProvider>
    );
  }
}
